import { useState, useEffect } from 'react';

/**
 * Whether this environment can answer a media query at all. jsdom cannot — it ships no
 * `matchMedia` — so without this every component that asks about the viewport would throw the
 * moment a unit test rendered it. Returning "no match" is the right answer there anyway: a
 * media query that cannot be evaluated has not matched.
 */
const supported = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/**
 * Subscribes to a CSS media query and returns whether it currently matches.
 * Updates reactively when the viewport changes.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => supported() && window.matchMedia(query).matches);

  useEffect(() => {
    if (!supported()) return;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);

    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
